import React from 'react';

/**
 * HeatmapGrid - Displays a 12x24 grid showing time-of-use periods
 * @param {Array<Array<number>>} schedule - 12x24 array (months x hours)
 * @param {string} title - Title for the heatmap
 * @param {Object} periodColors - Mapping of period index to color
 */
const HeatmapGrid = ({ schedule, title = "Schedule", periodColors = {} }) => {
  if (!schedule || !Array.isArray(schedule) || schedule.length === 0) {
    return (
      <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500">
        Schedule data not available
      </div>
    );
  }

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const hours = Array.from({ length: 24 }, (_, i) => i);

  // Default color scheme if not provided
  const getColor = (period) => {
    if (periodColors[period]) {
      return periodColors[period];
    }
    
    // Default gradient: blue (low) -> yellow (mid) -> red (high)
    const colors = [
      'bg-blue-100 text-blue-800',
      'bg-blue-200 text-blue-900',
      'bg-yellow-100 text-yellow-800',
      'bg-yellow-200 text-yellow-900',
      'bg-orange-200 text-orange-900',
      'bg-red-200 text-red-900',
    ];
    
    return colors[period % colors.length] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="w-full overflow-x-auto">
      <h4 className="text-lg font-semibold text-gray-700 mb-4">{title}</h4>
      
      <div className="inline-block min-w-full">
        <div className="flex">
          {/* Month labels column */}
          <div className="flex flex-col">
            <div className="h-8 w-16 border-b border-r border-gray-300 bg-gray-50"></div>
            {months.map((month, idx) => (
              <div
                key={month}
                className="h-8 w-16 flex items-center justify-center text-xs font-medium text-gray-600 border-b border-r border-gray-300 bg-gray-50"
              >
                {month}
              </div>
            ))}
          </div>

          {/* Grid cells */}
          <div className="flex-1">
            {/* Hour labels row */}
            <div className="flex">
              {hours.map((hour) => (
                <div
                  key={`hour-${hour}`}
                  className="h-8 flex-1 flex items-center justify-center text-xs font-medium text-gray-600 border-b border-gray-300 bg-gray-50"
                  style={{ minWidth: '32px' }}
                >
                  {hour}
                </div>
              ))}
            </div>

            {/* Data rows */}
            {schedule.map((monthData, monthIdx) => (
              <div key={`month-${monthIdx}`} className="flex">
                {Array.isArray(monthData) && monthData.map((period, hourIdx) => (
                  <div
                    key={`cell-${monthIdx}-${hourIdx}`}
                    className={`h-8 flex-1 flex items-center justify-center text-xs font-semibold border-b border-r border-gray-300 cursor-default ${getColor(period)}`}
                    style={{ minWidth: '32px' }}
                    title={`${months[monthIdx]}, Hour ${hourIdx}: Period ${period}`}
                  >
                    {period}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <div className="text-sm font-medium text-gray-600 mb-2">Period Legend:</div>
        <div className="flex flex-wrap gap-3">
          {[...new Set(schedule.flat())].sort((a, b) => a - b).map((period) => (
            <div key={`legend-${period}`} className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded ${getColor(period)} border border-gray-300 flex items-center justify-center text-xs font-semibold`}>
                {period}
              </div>
              <span className="text-xs text-gray-600">Period {period}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HeatmapGrid;
